/**
 * Module-level store for File objects that need to survive a React Router
 * navigation. React Router v7 serialises location.state via JSON, so File
 * objects lose their blob data. This store keeps the real File references
 * in memory and lets the target page consume them once on mount.
 *
 * Use peekPendingUploadFiles() for non-destructive reads (safe in React
 * StrictMode double-render) and clearPendingUploadFiles() when the files
 * have been handed off successfully.
 */

let pending: File[] | undefined;

export const setPendingUploadFiles = (files: File[] | undefined): void => {
  pending = files ? [...files] : undefined;
};

/** Non-destructive read — safe to call in useState initialisers that
 *  may run more than once under React StrictMode. */
export const peekPendingUploadFiles = (): File[] | undefined =>
  pending ? [...pending] : undefined;

/** Clear the store once the files have been consumed (e.g. after the
 *  initial send succeeds or on mount inside a useEffect). */
export const clearPendingUploadFiles = (): void => {
  pending = undefined;
};

/** @deprecated Prefer peekPendingUploadFiles + clearPendingUploadFiles. */
export const consumePendingUploadFiles = (): File[] | undefined => {
  const files = pending;
  pending = undefined;
  return files;
};
